import { Link, router } from "@inertiajs/react";
import { Button, Callout, Flex, Heading, Text } from "@radix-ui/themes";
import classNames from "classnames";
import { AlertCircle, Check } from "lucide-react";
import { type ReactNode } from "react";
import type { OnboardingShellProps } from "@/types/onboarding";

interface Props extends OnboardingShellProps {
  title: string;
  subtitle: string;
  children: ReactNode;
}

// The frame around every onboarding step: where you are, what's being asked,
// and a way back or past it. The step's own form supplies the Continue button.
export default function OnboardingShell({
  steps,
  back_path,
  skip_path,
  errors,
  title,
  subtitle,
  children,
}: Props) {
  return (
    <Flex direction="column" gap="5">
      <ol className="m-0 flex list-none flex-wrap gap-x-5 gap-y-2 p-0">
        {steps.map((step, i) => (
          <li
            key={step.key}
            className={classNames(
              "flex items-center gap-2 text-sm",
              step.state === "current"
                ? "font-semibold text-gray12"
                : "text-gray11",
            )}
            aria-current={step.state === "current" ? "step" : undefined}
          >
            <span
              className={classNames(
                "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                step.state === "done" && "bg-accent9 text-accent-contrast",
                step.state === "current" &&
                  "border-2 border-accent9 bg-panel text-accent11",
                step.state === "upcoming" && "bg-gray3 text-gray11",
              )}
            >
              {step.state === "done" ? (
                <Check size={14} aria-label="Done" />
              ) : (
                i + 1
              )}
            </span>
            {step.label}
          </li>
        ))}
      </ol>

      <Flex direction="column" gap="2">
        <Heading size="8">{title}</Heading>
        <Text size="4" color="gray">
          {subtitle}
        </Text>
      </Flex>

      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert">
          <Callout.Icon>
            <AlertCircle size={16} />
          </Callout.Icon>
          <Callout.Text>{errors.join(" ")}</Callout.Text>
        </Callout.Root>
      )}

      {children}

      <Flex justify="between" align="center" gap="3" wrap="wrap">
        {back_path ? (
          <Button asChild variant="ghost" color="gray" size="3">
            <Link href={back_path}>Back</Link>
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="ghost"
          color="gray"
          size="3"
          onClick={() => router.post(skip_path)}
        >
          {back_path ? "Skip this step" : "Skip for now"}
        </Button>
      </Flex>
    </Flex>
  );
}
